import { Box, Checkbox, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setFilter } from 'app/slice/executive.slice'
import type { ExecutiveState } from 'app/slice/executive.slice'
import { useBranches } from 'features/executive/shared/hooks/use-reference-data'
import { colors } from 'constants/theme'
import { EXEC_SELECT_MENU_PROPS, EXEC_SELECT_SX } from './filterInputStyles'

interface Props {
  userScope: 'own-branch' | 'all-branches'
  userBranchIds?: number[] // empty or undefined = all
}

const ALL_SENTINEL = 'ALL'

// Convert the slice value (`number | string | null`) to a stable array used
// by the MUI multi-Select. Null → ['ALL']; number → [n]; CSV → [a, b, ...].
function toSelectionArray(branchId: number | string | null): (number | typeof ALL_SENTINEL)[] {
  if (branchId == null) return [ALL_SENTINEL]
  if (typeof branchId === 'number') return [branchId]
  return branchId
    .split(',')
    .map((s) => parseInt(s, 10))
    .filter(Number.isFinite)
}

// Reduce a new selection (after user click) back to the slice's value shape.
// Mirrors dashboard.tsx handleChangeFilterSelect: clicking ALL resets to
// ['ALL']; selecting any branch removes ALL from the set; empty resets to ALL.
function fromSelectionArray(
  picked: (number | string)[]
): number | string | null {
  if (picked.length === 0) return null
  const last = picked[picked.length - 1]
  if (last === ALL_SENTINEL) return null
  const ids = picked.filter((v): v is number => typeof v === 'number')
  if (ids.length === 0) return null
  if (ids.length === 1) return ids[0]
  return ids.join(',')
}

export default function FilterBranchSelect({ userScope, userBranchIds }: Props) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const branchId = useSelector(
    (s: { executive: ExecutiveState }) => s.executive.filters.branchId
  )

  const { data: branches, loading } = useBranches({ scope: userScope, userBranchIds })

  const selection = toSelectionArray(branchId)
  const isAll = selection[0] === ALL_SENTINEL
  // "ทั้งหมด" is always offered — even own-branch users can pick it to view
  // the union of the branches they're allowed to see (useBranches already
  // restricts the option list to userBranchIds, so the backend query is still
  // scoped). Set canUseAll=false to fall back to the legacy hide-for-
  // own-branch behavior.
  const canUseAll = true
  // "ทั้งหมด" checkbox should also tick when the user has manually picked
  // every available branch — functionally the same as the all-branches state.
  const pickedIds = isAll ? [] : (selection as number[])
  const effectivelyAll =
    isAll || (branches.length > 0 && pickedIds.length === branches.length)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {t('EXECUTIVE.FILTER.BRANCH')}
        </Typography>
        <Tooltip
          title={t('EXECUTIVE.FILTER.DESC.BRANCH')}
          arrow
          placement="bottom"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360 } } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Select
        multiple
        size="small"
        value={selection as unknown as string[]}
        disabled={loading && branches.length === 0}
        onChange={(e) => {
          const next = e.target.value as unknown as (number | string)[]
          // Own-branch users can't select "all" — strip the sentinel and fall
          // back to their first allowed branch if nothing else remains.
          if (!canUseAll) {
            const ids = next.filter((v): v is number => typeof v === 'number')
            const value = ids.length === 0 ? branches[0]?.id ?? null
              : ids.length === 1 ? ids[0]
              : ids.join(',')
            dispatch(setFilter({ key: 'branchId', value }))
            return
          }
          dispatch(setFilter({ key: 'branchId', value: fromSelectionArray(next) }))
        }}
        MenuProps={EXEC_SELECT_MENU_PROPS}
        // Fixed width keeps the filter bar layout stable regardless of how
        // many branches are picked. Long single-branch names ellipsize via
        // MuiSelect-select's default text-overflow.
        sx={{ width: 220, ...EXEC_SELECT_SX }}
        renderValue={(picked) => {
          const arr = picked as unknown as (number | string)[]
          if (arr.length === 0 || arr[0] === ALL_SENTINEL || effectivelyAll) {
            return t('EXECUTIVE.FILTER.BRANCH_ALL')
          }
          if (arr.length === 1) {
            const b = branches.find((x) => x.id === arr[0])
            return b?.name ?? `#${arr[0]}`
          }
          // Multi-select: collapse to a count so the chip-line never
          // overflows the fixed-width Select.
          return `${arr.length} ${t('EXECUTIVE.FILTER.BRANCHES', 'สาขา')}`
        }}
      >
        {canUseAll && (
          <MenuItem value={ALL_SENTINEL}>
            <Checkbox
              size="small"
              checked={effectivelyAll}
              // Partial selection → render the minus dash so the user can see
              // at a glance that some-but-not-all branches are picked. Same
              // tri-state UX as the dashboard's doctor select.
              indeterminate={!effectivelyAll && pickedIds.length > 0}
              sx={{
                p: 0,
                mr: 1,
                '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: colors.themeSecondColor },
              }}
            />
            {t('EXECUTIVE.FILTER.BRANCH_ALL')}
          </MenuItem>
        )}
        {branches.map((b) => (
          <MenuItem key={b.id} value={b.id as unknown as string}>
            <Checkbox
              size="small"
              checked={effectivelyAll || pickedIds.includes(b.id)}
              sx={{
                p: 0,
                mr: 1,
                '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: colors.themeSecondColor },
              }}
            />
            {b.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}
